/**
 * Priority fit — the visual-truth secondary layer.
 *
 * Design contract:
 *   - Base pin color ALWAYS reflects safety/quality (green/amber/red) and
 *     NEVER shifts based on user preference. That's the core truth of the
 *     map and must stay stable across priority changes.
 *   - This module computes a separate priority-fit score per suburb × active
 *     priority. The main-simple renderer turns that score into a sharp halo
 *     behind the pin — but ONLY for the top 3–5 suburbs per priority. A
 *     "weak" or "neutral" fit produces no halo at all, so the eye is drawn
 *     to where to look first instead of being flooded with signal.
 *   - A Red pin with a top-5 commute halo tells the user: "good commute
 *     fit, but risky overall". The base red is preserved so the negative
 *     signal cannot be hidden.
 *
 * Rules enforced in this module:
 *   - `score` is the only thing main-simple sorts on when picking top
 *     matches. Higher = better fit. Ties are broken by dataset order.
 *   - `score` never factors verdict — a Red suburb can legitimately earn a
 *     high commute-fit score, and the halo will render on top of that Red
 *     base pin. That's the "good commute, risky overall" readout we want.
 *   - `tier` is kept for debug/logging parity, but the UI no longer paints
 *     a halo for "neutral" — only top-ranked good fits render.
 *   - Pure function — no DOM, no subscriptions, no side effects.
 */

import type { Verdict } from "../data/verdicts";
import type { Priority } from "./constraint-priority";
import { extractFacts } from "./suburb-facts";

export type FitTier = "good" | "neutral" | "weak";

export interface PriorityFit {
  tier: FitTier;
  /** Short label surfaced in debug + future tooltip work. */
  label: string;
  /** Halo color — always a positive tint, never red/green/amber. */
  haloColor: string;
  /**
   * Rank score for the active priority, 0–10. Higher = better fit. Used by
   * main-simple to pick the top 3–5 halos per priority. Score is computed
   * independently of verdict so Red suburbs can still rank for commute.
   */
  score: number;
}

/* -------------------------------------------------------------------------- */
/*  Per-priority fit logic                                                    */
/* -------------------------------------------------------------------------- */

function commuteFit(v: Verdict): PriorityFit {
  const f = extractFacts(v).transport;
  const tags = new Set((v.tags || []).map((t) => t.toLowerCase()));

  // Score ladder — rail + short commute is the gold standard.
  let score = 0;
  if (f.hasRail && f.commuteQuality === "short") score = 10;
  else if (tags.has("cbd") || tags.has("inner-city")) score = 9;
  else if (f.hasRail && f.commuteQuality === "medium") score = 8;
  else if (tags.has("it-corridor")) score = 8;
  // Specific "rail but long commute" MUST come before the catch-all
  // `f.hasRail` branch below — otherwise the catch-all wins first and
  // we'd never score a known-long rail suburb correctly. The old order
  // was `else if (f.hasRail) 7; else if (f.hasRail && long) 5;` which
  // ESLint flagged as a dead branch.
  else if (f.hasRail && f.commuteQuality === "long") score = 5;
  else if (f.hasRail) score = 7; // rail, commute unknown — still worth flagging
  else if (f.busOnly) score = 2;
  else if (f.commuteQuality === "long" || tags.has("car-dependent")) score = 1;
  else score = 4;

  const tier: FitTier = score >= 7 ? "good" : score >= 4 ? "neutral" : "weak";

  let label: string;
  if (score === 10) label = "rail + short commute";
  else if (score === 9) label = "CBD / inner-city — short commute";
  else if (score === 8 && f.hasRail) label = "rail + medium commute";
  else if (score === 8) label = "IT corridor — short commute";
  else if (score === 7) label = "rail access";
  else if (score === 5) label = "rail, but long commute";
  else if (score === 4) label = "average commute";
  else if (score === 2) label = "bus-only to rail";
  else label = "car-first commute";

  return { tier, label, haloColor: "#7ED9C9", score };
}

function schoolFit(v: Verdict): PriorityFit {
  const f = extractFacts(v).school;
  const tags = new Set((v.tags || []).map((t) => t.toLowerCase()));

  let score = 0;
  // "family" tag on top of a real catchment is the strongest school signal.
  if (f.hasCatchment && tags.has("family")) score = 10;
  else if (f.hasCatchment) score = 9;
  else if (f.privateOnly) score = 6; // real school, but private-only is a gate
  else if (f.emerging) score = 4;
  else if (f.disclaimed) score = 0;
  else score = 3;

  const tier: FitTier = score >= 7 ? "good" : score >= 4 ? "neutral" : "weak";

  let label: string;
  if (score === 10) label = "family + school zone";
  else if (score === 9) label = "zoned to public schools";
  else if (score === 6) label = "private schools only";
  else if (score === 4) label = "primaries in, secondary forming";
  else if (score === 0) label = "no public-school zone advantage";
  else label = "mixed school signal";

  return { tier, label, haloColor: "#7CC47F", score };
}

function lifestyleFit(v: Verdict): PriorityFit {
  const tags = new Set((v.tags || []).map((t) => t.toLowerCase()));

  // Stacking signals — the more lifestyle facets a suburb carries, the
  // stronger the fit. A beachfront cafe-culture suburb should outrank a
  // plain "cafe-culture" one.
  const strong = ["nightlife", "cafe-culture", "food-scene", "bars"];
  const scene = ["beach", "waterfront", "entertainment", "cultural"];
  const walkable = ["activity-centre", "retail-hub", "village"];

  const strongHits = strong.filter((x) => tags.has(x)).length;
  const sceneHits = scene.filter((x) => tags.has(x)).length;
  const walkableHits = walkable.filter((x) => tags.has(x)).length;

  let score = 0;
  if (strongHits >= 2 || (strongHits >= 1 && sceneHits >= 1)) score = 10;
  else if (strongHits >= 1) score = 8;
  else if (sceneHits >= 1 && walkableHits >= 1) score = 8;
  else if (sceneHits >= 1) score = 7;
  else if (walkableHits >= 1) score = 7;
  else if (tags.has("quiet") || tags.has("low-amenity") || tags.has("leafy"))
    score = 2;
  else score = 4;

  const tier: FitTier = score >= 7 ? "good" : score >= 4 ? "neutral" : "weak";

  let label: string;
  if (score === 10) label = "deep lifestyle stack";
  else if (score === 8) label = "strong lifestyle fit";
  else if (score === 7) label = "walkable village / scene";
  else if (score === 4) label = "average lifestyle";
  else label = "quiet residential";

  return { tier, label, haloColor: "#E8A87C", score };
}

/* -------------------------------------------------------------------------- */
/*  Public                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * Returns the priority-fit tier + score for the active priority. When
 * priority is "none", returns a zero-score neutral so the halo layer has
 * nothing to highlight (main-simple picks top-N > 0 only on real priorities).
 */
export function priorityFit(v: Verdict, priority: Priority): PriorityFit {
  switch (priority) {
    case "commute":  return commuteFit(v);
    case "school":   return schoolFit(v);
    case "lifestyle":return lifestyleFit(v);
    case "none":
    default:
      return {
        tier: "neutral",
        label: "no priority",
        haloColor: "rgba(0,0,0,0)",
        score: 0,
      };
  }
}

/**
 * Minimum score required for a suburb to be eligible as a "top match". Even
 * when fewer than TOP_MATCH_COUNT suburbs clear this bar, we still cap at
 * whatever number cleared — better to show 2 confident halos than 5 with 3
 * of them weak.
 */
export const GOOD_FIT_SCORE_FLOOR = 7;

/**
 * Number of suburbs that can receive a halo per active priority. Keep small
 * — the halo exists to answer "where should I look first", so more than ~5
 * defeats the purpose.
 */
export const TOP_MATCH_COUNT = 5;

/**
 * Given a dataset and a priority, return the set of suburb IDs that earn a
 * halo. Rules:
 *   - priority === "none"   → empty set (no halo on the map).
 *   - otherwise             → up to TOP_MATCH_COUNT suburbs with score
 *                             >= GOOD_FIT_SCORE_FLOOR, ranked by score
 *                             (ties broken by dataset order for stability).
 *   - Verdict is NOT a filter. A Red suburb with a top-5 commute score
 *     still gets a halo — the halo is an emphasis layer, not a "safe
 *     picks" layer. The base Red pin preserves the negative signal.
 */
export function selectTopMatches(
  dataset: readonly Verdict[],
  priority: Priority
): Set<string> {
  if (priority === "none") return new Set();
  const ranked = dataset
    .map((v, idx) => ({ v, idx, fit: priorityFit(v, priority) }))
    .filter((r) => r.fit.score >= GOOD_FIT_SCORE_FLOOR)
    .sort((a, b) => {
      if (b.fit.score !== a.fit.score) return b.fit.score - a.fit.score;
      return a.idx - b.idx; // stable — preserve dataset order on ties
    })
    .slice(0, TOP_MATCH_COUNT);
  return new Set(ranked.map((r) => r.v.id));
}
